import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import type { Settings } from './lib/settings';
import { defaultSettings } from './lib/settings';

// DEBUG — mock auth locally. REMOVE BEFORE COMMIT.
const DEBUG_MOCK_AUTH = false;

export interface UserRow {
  id: string;
  email: string;
  cal_token: string;
  lat: number;
  lng: number;
  settings: Settings;
  updated_at: string;
}

interface Store {
  session: Session | null;
  user: User | null;
  row: UserRow | null;
  loading: boolean;
  init: () => Promise<void>;
  setRow: (row: UserRow | null) => void;
  signOut: () => Promise<void>;
}

export const useStore = create<Store>((set) => ({
  session: null,
  user: null,
  row: null,
  loading: true,

  async init() {
    if (DEBUG_MOCK_AUTH) {
      set({
        session: { user: { id: 'debug', email: 'debug@example.com' } } as Session,
        user: { id: 'debug', email: 'debug@example.com' } as User,
        row: { id: 'debug', email: 'debug@example.com', cal_token: 'debug-token', lat: 37.77, lng: -122.42, settings: defaultSettings(), updated_at: new Date().toISOString() },
        loading: false,
      });
      return;
    }

    const { data } = await supabase.auth.getSession();
    set({ session: data.session, user: data.session?.user ?? null });

    // When Supabase tells us the session changed (sign-in, sign-out, token
    // refresh) we need to re-pull the user row — otherwise /manage can
    // render with a live session but a stale null row (classic post-auth
    // race on first hit after verifyOtp).
    //
    // The callback must NOT be async and must NOT await any supabase call
    // directly: Supabase runs this handler while holding its internal auth
    // lock, and any supabase request from inside it tries to re-acquire that
    // lock and deadlocks. Symptom: switching tabs re-emits SIGNED_IN on
    // focus, fetchRow hangs, and the UI is stuck on "Loading…" forever. So we
    // defer the fetch out of the lock with setTimeout.
    supabase.auth.onAuthStateChange((event, session) => {
      set({ session, user: session?.user ?? null });
      if (!session) {
        set({ row: null, loading: false });
        return;
      }
      if (event === 'TOKEN_REFRESHED') return;
      // SIGNED_IN re-fires every time the tab regains focus. If we already
      // have the row there's nothing to reload — don't flash the splash.
      if (event === 'SIGNED_IN' && useStore.getState().row) return;
      set({ loading: true });
      setTimeout(() => void fetchRow(set), 0);
    });

    if (data.session?.user) await fetchRow(set);
    else set({ loading: false });
  },

  setRow(row) { set({ row }); },

  async signOut() {
    await supabase.auth.signOut();
    set({ session: null, user: null, row: null });
  },
}));

async function fetchRow(set: (partial: Partial<Store>) => void) {
  const { data, error } = await supabase.from('users').select('*').maybeSingle();
  if (error) console.error('failed to load user row', error);
  set({ row: (data as UserRow | null) ?? null, loading: false });
}

export async function refreshRow() {
  const { data, error } = await supabase.from('users').select('*').maybeSingle();
  if (error) console.error('failed to refresh user row', error);
  useStore.getState().setRow((data as UserRow | null) ?? null);
}
